import { ClientSideError, ClientSideErrorType, cloneErrorObject, logger } from '@dtx-company/logger'
import { FormProvider, useForm } from 'react-hook-form'
import { InternalPlanPurchaseFormContext } from './context/InternalPlanPurchaseFormContext'
import { InternalPlanPurchaseFormContextProps } from './context/InternalPlanPurchaseFormContextProps'
import { PaymentMethod } from '@stripe/stripe-js'
import { PlanPurchaseFormData } from './PlanPurchaseFormData'
import { PlanPurchaseFormProps } from './PlanPurchaseFormProps'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { StripeElements } from '@app/common/src/components/PlanPurchaseExperience/components/StripeElements'
import { buySubscriptionV3 } from '@app/common/src/components/PlanPurchaseExperience/utils/buySubscriptionV3'
import { createPaymentMethodFromFormV2 } from '@app/common/src/components/PaymentModal/PaymentModal.utils'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { refreshStripePaymentElementForPaymentTypes } from '@app/common/src/components/PlanPurchaseExperience/constants'
import { sendErrorNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCohort } from '@dtx-company/inter-app/src/hooks/cohorts/useCohort'
import { useEffect, useMemo, useState } from 'react'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { useHubSpotUserIthacaRoleSync } from '../../../../components/_app/hooks/useHubSpotUserSync'
import { useInternalPlanPurchaseFormContext } from './context/useInternalPlanPurchaseFormContext'
import { useInternalPlanPurchaseProviderContext } from '../purchase/PlanPurchaseProvider/context/useInternalPlanPurchaseProviderContext'
import { useOnce } from '@dtx-company/true-common/src/hooks/useOnce'
import { usePaymentMethodError } from '@app/common/src/components/PlanPurchaseExperience/usePaymentMethodError'
import { usePlanCostSummarySWR } from '@app/common/src/hooks/usePlanCostSummarySWR'
import { usePlanPurchaseExperienceSubscriptionInfo } from '@app/common/src/components/PlanPurchaseExperience/hooks/usePlanPurchaseExperienceSubscriptionInfo'
import { usePlanPurchaseFormPropsToSetFormValues } from './hooks/usePlanPurchaseFormPropsToSetFormValues/usePlanPurchaseFormPropsToSetFormValues'
import { usePlanPurchaseFormSchema } from './hooks/usePlanPurchaseFormSchema/usePlanPurchaseFormSchema'
import { useRefreshTokenAfterSubscriptionChange } from '@app/common/src/components/PlanPurchaseExperience/utils/useRefreshTokenAfterSubscriptionChange'
import { yupResolver } from '@hookform/resolvers/yup'
import events from '@dtx-company/inter-app/src/event-tracking/events/proConversion'

function InnerPlanPurchaseForm({
  children,
  currentPlanId,
  token,
  className,
  style,
  conversionSource,
  existingPaymentMethod,
  heapId,
  lockPrismicId,
  onSuccessfulCheckout,
  couponDiscountInCents
}: PlanPurchaseFormProps): JSX.Element {
  const { planType, selectedPlan } = useInternalPlanPurchaseProviderContext()
  const { form, paymentMethodType, setIsSubmitted } = useInternalPlanPurchaseFormContext()
  const { currentSubscriptionInfoData } = usePlanPurchaseExperienceSubscriptionInfo()
  const authState = useAuthState()
  const { name: cohortName } = useCohort()
  const { refreshTokenAfterSubscriptionChange } = useRefreshTokenAfterSubscriptionChange()

  const { handleSubmit } = form
  const values = form.watch()
  const elements = useElements()
  const stripe = useStripe()

  const orgName = values.orgName || authState.jwt?.email

  useOnce(() => {
    if (!selectedPlan || !authState.isAuthChecked) {
      return false
    }

    events.userStartedProConversion({
      email: authState.jwt?.email ?? '',
      lockPrismicId,
      planType: planType,
      // per Andrew: this is to show which plan the user is initially presented with
      // the PlanPurchaseForm does not assume there's a single plan to start with, but in
      // practice our implementations of this will provide enough initial data to have one
      // since checkout flows should require as few decisions as possible
      planSelected: selectedPlan.id,
      currentUserPlan: currentPlanId,
      codeLimit: authState.codeLimit,
      pageLimit: authState.pageLimit,
      conversionSource,
      isLoggedIn: authState.isAuthenticated,
      conversionType: 'upgrade'
    })
  })

  const { sendPaymentMethodErrorMessage } = usePaymentMethodError()

  // HubSpot user sync. Update hubspot users with new Ithaca role (new plan)
  useHubSpotUserIthacaRoleSync()

  const onSubmit = handleSubmit(
    async data => {
      if (!elements || !stripe || !selectedPlan || !token) return
      setIsSubmitted(true)
      let updatedSubscriptionId: string | undefined

      try {
        const paymentMethod = await (async (): Promise<PaymentMethod | undefined> => {
          if (values.paymentElementComplete && stripe && elements) {
            return await createPaymentMethodFromFormV2({ elements, stripe })
          } else if (existingPaymentMethod) {
            return existingPaymentMethod
          } else {
            const error = new ClientSideError({
              name: 'missing-payment-method',
              type: ClientSideErrorType.ClientSideValidationError,
              message: 'No payment method'
            })
            logger.logError(error, { technicalArea: 'monetization' })
            throw error
          }
        })()

        updatedSubscriptionId = await buySubscriptionV3({
          token,
          selectedPlanId: selectedPlan.id,
          currentPlanId,
          paymentMethodId: paymentMethod?.id,
          paymentMethodType: paymentMethodType,
          promoCode: values.promoCode,
          stripe,
          orgName,
          planExtraDetails: values.planExtraDetails,
          subscriptionId: currentSubscriptionInfoData?.id
        })

        events.userCompletedProConversion({
          planSelected: selectedPlan.id,
          previousPlan: currentPlanId,
          promoCode: values.promoCode,
          subscriptionId: updatedSubscriptionId,
          cohort: cohortName,
          lockPrismicId,
          email: authState.jwt?.email ?? '',
          ithacaId: authState.jwt?.ithacaId ?? '',
          planPrice: selectedPlan.priceInCents,
          conversionSource: conversionSource,
          isLoggedIn: authState.isAuthChecked && authState.isAuthenticated,
          conversionType: 'upgrade',
          planDetails: undefined,
          paymentMethodType,
          couponDiscountInCents
        })

        fireAnalyticsEvent(`${heapId}_PlanPurchaseForm_Submitted`, {
          error: null,
          orgName,
          paymentElementComplete: values.paymentElementComplete
        })

        await refreshTokenAfterSubscriptionChange(
          updatedSubscriptionId || currentSubscriptionInfoData?.id
        )
      } catch (error) {
        // If there's an error, allow re-submit
        // But if successful, don't allow re-submit to avoid double purchases
        // Implementations should navigate away to another page or state and expect
        // the submit button to stay disabled
        setIsSubmitted(false)

        if (!error.type) {
          logger.logError(
            new ClientSideError({
              ...cloneErrorObject(error),
              type: ClientSideErrorType.DefaultError
            }),
            { team: 'monetization' }
          )
        }
        await refreshTokenAfterSubscriptionChange(
          updatedSubscriptionId || currentSubscriptionInfoData?.id
        )
        sendPaymentMethodErrorMessage(error)
        return // abort
      }

      await onSuccessfulCheckout()
    },
    () => {
      fireAnalyticsEvent(`${heapId}_PlanPurchaseForm_Submitted`, {
        error: 'invalid',
        orgName,
        paymentElementComplete: values.paymentElementComplete
      })
      sendErrorNotification('Please fill the missing fields to purchase')
    }
  )

  return (
    <form method="post" action="" onSubmit={onSubmit} className={className} style={style}>
      {children}
    </form>
  )
}

/**
 * Provides a form architecture for allowing plan purchases.
 *
 * The content can be completely customized.
 * The required fields can be passed as props or as form fields. Use the
 * PlanPurchaseFormFieldWrapper for common form fields like TextField and Select.
 *
 * On submit, the plan will be purchased.
 */
export function PlanPurchaseForm(props: PlanPurchaseFormProps): JSX.Element {
  const { defaultValues, validationErrors, existingPaymentMethod } = props
  const { planType, selectedPlan } = useInternalPlanPurchaseProviderContext()

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [paymentMethodType, setPaymentMethodType] = useState<string | undefined>(undefined)
  const [mountPaymentElement, setMountPaymentElement] = useState(true)
  const schema = usePlanPurchaseFormSchema(
    validationErrors,
    existingPaymentMethod,
    paymentMethodType
  )
  const form = useForm<PlanPurchaseFormData, { planType: PlanType }>({
    resolver: yupResolver(schema),
    defaultValues,
    context: {
      planType
    }
  })
  usePlanPurchaseFormPropsToSetFormValues(props, form)

  const values = form.watch()
  const summarySWR = usePlanCostSummarySWR(
    () => {
      if (!props.token) return
      if (!selectedPlan) return
      return {
        token: props.token,
        newPlanId: selectedPlan.id,
        promoCode: values.promoCode,
        planExtraDetails: values.planExtraDetails
      }
    },
    {
      debounceInterval: 500,
      revalidateIfStale: false,
      revalidateOnFocus: false
    }
  )
  const planCost = summarySWR.data?.grandTotalInCents
  const couponDiscountInCents = summarySWR.data?.couponDiscountInCents

  useEffect(() => {
    if (summarySWR.isValidating || !summarySWR.data) return
    if (
      paymentMethodType &&
      refreshStripePaymentElementForPaymentTypes.includes(paymentMethodType)
    ) {
      // stripe payment element has a bug related to amount update. If payment method google pay is selected
      // and amount is changed, confirmation google modal window will be presented with an old value. If amount is changed
      // and then the payment method is changed to google pay, the amount displays as expected.
      // Remove this code when the Stripe bug is fixed
      setMountPaymentElement(false)
    }
  }, [paymentMethodType, summarySWR.isValidating, summarySWR.data])

  useEffect(() => {
    if (!mountPaymentElement) setMountPaymentElement(true)
  }, [mountPaymentElement])

  const context = useMemo<InternalPlanPurchaseFormContextProps>(() => {
    return {
      form,
      isSubmitted,
      paymentMethodType,
      setIsSubmitted,
      setPaymentMethodType,
      mountPaymentElement
    }
  }, [form, isSubmitted, mountPaymentElement, paymentMethodType])

  return (
    <StripeElements amount={planCost}>
      <InternalPlanPurchaseFormContext.Provider value={context}>
        <FormProvider {...form}>
          <InnerPlanPurchaseForm couponDiscountInCents={couponDiscountInCents} {...props} />
        </FormProvider>
      </InternalPlanPurchaseFormContext.Provider>
    </StripeElements>
  )
}
